import { Present } from '@element-plus/icons-vue'
import { reactive } from 'vue'

export function formatName(firstname, middlename, lastname, delimiter = '') {
  let name = ''
  delimiter = delimiter != '' ? delimiter : '/'
  if (!isNull(lastname)) name = lastname
  if (!isNull(firstname)) name = name + ('' != firstname ? delimiter + firstname : '')
  if (!isNull(middlename)) name += ' ' + middlename

  return name
}

// Currency metadata registry, populated at runtime from the backend's active currency
// list (CurrencyStore.fetchCurrencies() calls registerCurrencies() on load). Keyed by
// both currency code and numeric currency_id so legacy id-based lookups keep working.
// Nothing here should ever hardcode a specific currency code/id - unregistered lookups
// fall back to the raw input instead of guessing a currency.
// Reactive so templates calling formatCurrency()/priceFormat()/getCurrencyById() before
// registerCurrencies() has populated this (e.g. on first paint after a fresh page load)
// automatically re-render with the real symbol once it arrives, instead of getting stuck
// showing the raw code from that first render forever.
const currencyByCode = reactive({})
const currencyById = reactive({})

// Some legacy call sites pass a symbol instead of a code (e.g. 'U$'/'US$' for USD).
// This only normalizes the lookup key - the returned symbol still comes from the
// registered currency data, not a hardcoded value.
const CURRENCY_CODE_ALIASES = { 'U$': 'USD', 'US$': 'USD' }

export function registerCurrencies(currencies) {
  if (!Array.isArray(currencies)) return
  currencies.forEach((c) => {
    if (!c || !c.code) return
    const meta = {
      symbolLeft: c.symbolLeft ?? c.symbol_left ?? '',
      symbolRight: c.symbolRight ?? c.symbol_right ?? '',
      decimalPlace: c.decimalPlace ?? c.decimal_place ?? 2
    }
    currencyByCode[c.code.toUpperCase()] = meta
    const currencyId = c.currencyId ?? c.currency_id
    if (currencyId != null) {
      currencyById[currencyId] = meta
    }
  })
}

export function formatCurrency(currency) {
  const raw = (currency || '').toString().toUpperCase()
  const code = CURRENCY_CODE_ALIASES[raw] || raw
  const meta = currencyByCode[code]
  if (meta && (meta.symbolLeft || meta.symbolRight)) {
    return meta.symbolLeft || meta.symbolRight
  }
  return currency
}

export function getCurrencyById(currency_id) {
  const meta = currencyById[currency_id]
  return meta && (meta.symbolLeft || meta.symbolRight) ? meta.symbolLeft || meta.symbolRight : ''
}

export function formatAmount(num) {
  if (num == '' || !num) return 0
  let numericString = num ? num.toString().replace(/[^\d.,-]/g, '') : ''

  numericString = parseFloat(numericString.replace(/,/g, ''))

  numericString = numericString.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return numericString
}

export function decodeHTMLEntities(str) {
  const entityMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '='
  }

  return str.replace(/&[a-zA-Z0-9#]+;/g, function (match) {
    return entityMap[match] || match
  })
}

export function priceAmount(num, deci = 0) {
  let numericString = 0
  if (!(num == '' || !num)){
    let sign = num.toString().charAt(0) == '-' ? -1 : 1
    let nm = num.toString().replace(/,/g, '')
    let match = nm ? nm.match(/-?[\d\.]+/) : []
    numericString = (match && match.length > 0 ? parseFloat(match[0]) : 0) * sign
  }
  let res = deci == 0 ? Math.floor(1.0 * numericString) : numericString
  return res
}
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())
}
export function isValidCellphone(phoneNumber, ignorePnull = 1) {
  if (ignorePnull == 1 && phoneNumber == '') {
    return true
  }
  return /^\s*\(?([0-9]{1,4})\)?[-\.\s]?([0-9]{2,4})[-\.\s]?([0-9]{3,4})\s*$/.test(phoneNumber)
}

export function isValidName(name) {
  return /^[A-Za-z][A-Za-z ]{0,31}$/.test(name)
}

export function isValidDate(d) {
  const regex = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
  return regex.test(d)
}
export function checkDate(dateString) {
  const date = new Date(dateString + 'T00:00:00')

  if (date.toString() === 'Invalid Date') {
    return false
  }

  const [year, month, day] = dateString.split('-').map(Number)

  let result =
    date.getFullYear() * 1 === year * 1 &&
    (date.getMonth() + 1) * 1 === month * 1 &&
    date.getDate() * 1 === day * 1

  return result
}

export function getBirthdayType(bd, adultDate) {
  let res = 0
  let today = new Date().getFullstring()
  if(isValidDate(bd) && bd < today){
      if(new Date(bd+'T00:00:01').nextMonthDate(36,0).getFullstring() < today)
      {
          if(isValidDate(adultDate)){
            if(adultDate >= bd){
              res = 3        //adult
            }
            else{
              res = 2          //child
            }
          }
      }
      else{
        res = 1   //baby
      }
  }    
  return res
}

export function isValidBirthday(bd, cmpDate, type) {
  let noBabyBD = (bd && (new Date(bd+'T00:00:01').nextMonthDate(36,0).getFullstring() <  new Date().getFullstring()))
  let result =
    isValidDate(bd) &&
    isValidDate(cmpDate) &&
    ((type.toLowerCase() == 'child' && (cmpDate < bd) && noBabyBD ) ||
      (type.toLowerCase() == 'adults' && cmpDate >= bd))    
  return result
}

export function isValidBirthday2(birthday) {
  const today = new Date()
  const birthdayDate = new Date(birthday)

  return birthdayDate < today
}

export function isValidPassport(p) {
  return /^[A-Z][A-Z 0-9]{2,16}$/.test(p.toUpperCase())
}

export function isValidPassport_country(country, p) {
  if (country == 'CN') {
    const regex = /^(DE|SE|PE)\d{7}$|^E\d{8}$|^[DSPG]\d{8}$|^E[ABCDEFGHJKLMNPQRSTUVWXYZ]\d{7}$/
    return regex.test(p)
  } else if (country == 'CA')
    return (
      /^[A-Z]{2}[0-9]{6}$/.test(p.toUpperCase()) ||
      /^[A-Z]{1}[0-9]{6}[A-Z]{2}$/.test(p.toUpperCase())
    )
  else if (country == 'US') return /^[0-9]{9}$|^[A-Z][0-9]{8}$/.test(p.toUpperCase())
  else return true
}

export function isValidExp(exp, dp_aft_6mth) {
  return isValidDate(exp) && isValidDate(dp_aft_6mth) && dp_aft_6mth < exp
}
export function validatePhoneNumber(phoneNumber) {
  const pattern = /^\s*\(?([0-9]{1,4})\)?[-\.\s]?([0-9]{2,4})[-\.\s]?([0-9]{3,4})\s*$/

  if (pattern.test(phoneNumber)) {
    return true
  } else {
    return false
  }
}
export function priceFormat(currency, oriprice, thousand_point = ',', decimal = 2, zero = 0) {
  // Group every 3 digits (the previous loop printed 1,599,866.67 as "1599,866.67",
  // dropped inner zero padding and left 1000 ungrouped). Rules kept: a trailing ".00" is
  // dropped unless zero == 2; a zero amount renders '' unless `zero` is set.
  const prefix = (oriprice < 0 ? '-' : '') + (currency ? formatCurrency(currency) : '$')
  const price = Math.abs(Number(oriprice) || 0)
  if (!(price > 0) && !zero) {
    return ''
  }
  const sep = typeof thousand_point != 'undefined' ? thousand_point : ','
  // Zero-decimal currencies (JPY...) never show minor units.
  if (['JPY', 'KRW', 'HUF', 'TWD'].includes(String(currency || '').toUpperCase())) {
    decimal = 0
  }
  const fixed = price.toFixed(decimal)
  const dot = fixed.indexOf('.')
  const intPart = (dot >= 0 ? fixed.slice(0, dot) : fixed).replace(/\B(?=(\d{3})+(?!\d))/g, sep)
  let str = prefix + intPart + (dot >= 0 ? fixed.slice(dot) : '')
  if (price > 0 && str.slice(-3) == '.00' && zero != 2) {
    str = str.slice(0, str.length - 3)
  }
  return str
}
export function notEmpty(obj) {
  if (obj === null || obj === undefined) return false;
  const type = typeof obj;
  if (type === 'string') return obj.trim() !== '';
  if (type === 'number') return true; 
  if (Array.isArray(obj)) return obj.length > 0;
  if (obj instanceof Date) return !isNaN(obj.getTime()); 
  if (type === 'object') return Object.keys(obj).length > 0;
  return Boolean(obj); 
}
export function isNull(obj) {
  return (
    obj === null ||
    obj == undefined ||
    obj == 'undefined' ||
    obj == '' ||
    (obj.constructor === Object && Object.keys(obj).length === 0)
  )
}

export function scrollWindow(elem, pos = 'start') {
  if (elem && elem.scrollTop !== undefined) {
    elem.scrollIntoView({ behavior: 'smooth', block: pos })
  }
}

export function scrollWindowDiff(elem, topOffset) {
  if (elem && elem.scrollTop !== undefined) {
    const elementPosition = elem.getBoundingClientRect().top + window.pageYOffset - topOffset
    window.scrollTo({ top: elementPosition, behavior: 'smooth' })
  }
}

export function scrollToPosition(elem, adj = 0) {
  window.scrollTo(0, elem.offsetTop - adj)
}

export function productIdExt(pid, type = 1) {
  pid = parseInt(pid)
  if (pid < 1) return 0
  let mov = 6
  let adj = 2548
  return 1 == type ? ((pid + adj) << mov) + (pid % 2) : ((pid - (pid % 2)) >> mov) - adj
}

export function getDaysFromYYYYMM(_year, _month) {
  if (!_year || !_month) {
    return []
  }
  const month = parseInt(_month)
  const year = parseInt(_year)
  let daysInMonth = 31

  if (month === 4 || month === 6 || month === 9 || month === 11) {
    daysInMonth = 30
  } else if (month === 2) {
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      daysInMonth = 29
    } else {
      daysInMonth = 28
    }
  }
  return Array.from({ length: daysInMonth }, (v, k) => (k + 1).toString().padStart(2, '0'))
}

export function formatDateLocal(dateString, isDate = false,hasyear=1) {
  if (dateString == '' || dateString == undefined || dateString == 'undefined') return ''
  let date = new Date()
  if (!isDate) {
    dateString = dateString.trim()
    date = new Date(dateString.length>10?dateString.replace(' ','T'):(dateString+'T00:00:01'))
  } else {
    date = dateString
  }
  const day = date.getDate()
  const year = date.getFullYear()
  const month = date.getMonth()
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const monthName = monthNames[month]
  const result = monthName + ' ' + day + (hasyear?', '+year:'')
  return result
}

export function formatDateLocalMonthYear(dateString) {
  //转成 April 2024
  var parts = dateString.split('-')
  var year = parseInt(parts[0], 10)
  var month = parseInt(parts[1], 10) - 1
  var day = parseInt(parts[2], 10)

  var date = new Date(year, month, day)

  year = date.getFullYear()
  month = date.getMonth()

  var monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  var monthName = monthNames[month]

  var result = monthName + ' ' + year

  return result
}

export function formatDateLocalMulLan(dateString, t, locale, isDate = false) {
  // 转成 Apr 1, 2024
  if (dateString == '' || dateString == undefined || dateString == 'undefined') return ''
  var date = new Date()
  var day
  if (!isDate) {
    date = new Date(dateString.slice(0, 10)+'T00:00:01')
    if (isNaN(date.getTime())) {
      return t('common.invalidDate')
    }
    year = date.getFullYear()
    month = date.getMonth()
    day = date.getDate()
  } else {
    date = dateString
    day = date.getDate()
  }
  var year = date.getFullYear()
  var month = date.getMonth()
  var monthNames = [
    t('common.months.jan'),
    t('common.months.feb'),
    t('common.months.mar'),
    t('common.months.apr'),
    t('common.months.may'),
    t('common.months.jun'),
    t('common.months.jul'),
    t('common.months.aug'),
    t('common.months.sep'),
    t('common.months.oct'),
    t('common.months.nov'),
    t('common.months.dec')
  ]
  var monthName = monthNames[month]
  var result = monthName + ' ' + day + ', ' + year
  if (locale != 'en') result = year + t('common.yearExt') + monthName + day + t('common.dayExt')
  return result
}

export function formatDateLocalMonthYearMulLan(dateString, t, locale) {
  //转成 April 2024
  var parts = dateString.split('-')
  var year = parseInt(parts[0], 10)
  var month = parseInt(parts[1], 10) - 1
  var day = parseInt(parts[2], 10)

  var date = new Date(year, month, day)

  year = date.getFullYear()
  month = date.getMonth()

  var monthNames = [
    t('common.longMonths.jan'),
    t('common.longMonths.feb'),
    t('common.longMonths.mar'),
    t('common.longMonths.apr'),
    t('common.longMonths.may'),
    t('common.longMonths.jun'),
    t('common.longMonths.jul'),
    t('common.longMonths.aug'),
    t('common.longMonths.sep'),
    t('common.longMonths.oct'),
    t('common.longMonths.nov'),
    t('common.longMonths.dec')
  ]
  var monthName = monthNames[month]
  var result = monthName + ' ' + year
  if (locale != 'en') result = year + t('common.yearExt') + '' + monthName

  return result
}
export function monthFullString(d) {
  var months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  return months[d.getMonth()]
}

export function getAgeRange(item) {
  if (!item.hasOwnProperty('agerange') || isNull(item.agerange)) return ''
  if (item.agerange * 1 == 1) return 'All Ages'
  else if (item.agerange * 1 == 2) return '5 to 80 years old'
  else if (item.agerange * 1 == 3) return '8 to 80 years old'
  else if (item.agerange * 1 == 4) return '10 to 80 years old'
  else if (item.agerange * 1 == 5) return '15 to 80 years old'
  else if (item.agerange * 1 == 6) return '18 to 65 years old'
  else return ''
}

export function getGender(value) {
  if (value * 1 == 1) return 'Male'
  else if (value * 1 == 2) return 'Female'
  else if (value * 1 == 8) return 'Undisclosed'
  else if (value * 1 == 0) return 'Unspecified'
  else if (value * 1 == 3) return 'Mrs'
  else if (value * 1 == 4) return 'Female'
  else if (value * 1 == 5) return 'Male'
  else if (value * 1 == 6) return 'Female'
  else if (value * 1 == 7) return 'Infant'
}
export function getRoomPriceList(roomlist, pModel, prices, cityprice, fpTrip) {
  let tripPrice = 0
  let tripPriceDetails = []

  Object.values(roomlist).forEach((item) => {
    let rmpList = getRoomPrice(item, pModel, prices, cityprice, fpTrip ? 1 : 0)
    let curroomprice = parseFloat(rmpList.totalPrice)
    tripPrice += curroomprice
    if (rmpList.details) {
      if (tripPriceDetails.length < 1) {
        tripPriceDetails = rmpList.details
      } else {
        for (let j in rmpList.details) {
          let noExist = true
          for (let k in tripPriceDetails) {
            if (
              rmpList.details[j].title == tripPriceDetails[k].title &&
              rmpList.details[j].price == tripPriceDetails[k].price
            ) {
              noExist = false
              tripPriceDetails[k].qty += rmpList.details[j].qty
              break
            }
          }
          if (noExist) {
            tripPriceDetails.push(rmpList.details[j])
          }
        }
      }
    }
  })

  return tripPriceDetails
}
export function getRoomPrice(roomItem, pModel, prices, cityprice, citypriceCeof = 1) {
  let totalPrice = 0
  let details = []

  for(let pax of roomItem.travelers )
  {
    pax['tourPrice'] = {roomprice: 0};
  }
  if (pModel) {
    let pricemodel = {}
    for (var i in pModel) {
      let pm = pModel[i]
      if (
        roomItem.adult == pm.adult &&
        roomItem.child == pm.child &&
        pm.child_nopax == 0 &&
        pm.infant == 0 &&
        pm.infant_nopax == 0
      ) {
        pricemodel = pModel[i]
        break
      }
    }
    for (var i = 1; i < 7; i++) {
      let qty = parseInt(pricemodel['ceof' + i])
      let curp = parseFloat(prices['price' + i])
      let title = prices.name
      let tqty = parseInt(pricemodel['ceof' + i])
      if (qty > 0 && curp != 0) {
        totalPrice += curp * qty
        if (i == 5) {
          title = 'Solo Occupancy'
          for(let tq=0; tq<tqty; tq++){          
            roomItem.travelers[0]['tourPrice']['soloprice'] = curp;
          }
        } else {
          for(let tq=0; tq<tqty; tq++){
            for(let pax of roomItem.travelers) {
               if(pax['tourPrice']['roomprice'] === 0){
                 pax['tourPrice']['roomprice'] = curp;
                 break;
               }
            }
          }
          curp += parseFloat(cityprice) * citypriceCeof
        }
        let noExist = true
        for (let j in details) {
          if (details[j].title == title && details[j].price == curp) {
            noExist = false
            details[j].qty += qty
            break
          }
        }
        if (noExist) {
          details.push({ title: title, price: curp, qty: qty })
        }
      }
    }
  }
  return { totalPrice: totalPrice, details: details}
}

export function getNextMonth(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number)

  const date = new Date(year, month, 1)

  date.setMonth(date.getMonth())

  const nextYear = date.getFullYear()
  const nextMonth = date.getMonth() + 1

  // 格式化为 "YYYY-MM"
  return `${nextYear}-${nextMonth.toString().padStart(2, '0')}`
}

export function getPreMonth(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number)

  const date = new Date(year, month - 1, 1)

  date.setMonth(date.getMonth() - 1)

  const previousYear = date.getFullYear()
  const previousMonth = date.getMonth() + 1

  // 格式化为 "YYYY-MM"
  return `${previousYear}-${previousMonth.toString().padStart(2, '0')}`
}

export function jsPost(url, data, target = '_blank') {
  /*
   *  example :   data = {'common_order_id':5555};
   */
  var myForm = document.createElement('FORM')
  myForm.method = 'POST'
  myForm.action = url
  myForm.setAttribute('id', 'jsPostForm')
  myForm.setAttribute('target', target)
  document.body.appendChild(myForm)
  for (var name in data) {
    var myInput = document.createElement('INPUT')
    myInput.setAttribute('name', name)
    myInput.setAttribute('type', 'hidden')
    myInput.setAttribute('value', data[name])
    document.getElementById('jsPostForm').appendChild(myInput)
  }
  myForm.submit()
  document.getElementById('jsPostForm').remove()
}
// export function pagelog(r, p, info = {}) {
//   if (r && p) {
//     info = {...info,time:Math.floor(Date.now() / 1000)};
//     r.webPost({ page: p, info: info }, (res) => {}, 'account/account/pagelog')
//   }
// }
export function slackMsg(r, info = '',slackcode=8) {
  if (r && info!=='') {
    r.webPost({ slackcode:slackcode,info: info }, (res) => {}, 'account/account/slackMsg')
  }
}
export function removeTags(str) {
  return str.replace(/<\/?[^>]+(>|$)/g, '')
}

export function changeLanToId(lan) {
  if (!lan) return 1
  else if (lan == 'en') return 1
  else if (lan == 'zh_cn') return 3
  else if (lan == 'zh_tw') return 4
}

export function capitalizeFirstLetter(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function getEasternTimeString() {
  let now = new Date();
  let formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  let parts = formatter.formatToParts(now);
  let get = name => parts.find(p => p.type === name).value;
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
}

// Decode the human-readable city/airport name fields of a flight order payload
// (shared shape used by the invoice and bookings flight tables) in place. The
// backend stores some names HTML-entity-encoded (e.g. "Xi&#39;an") which Vue
// text interpolation would otherwise render raw. Reuses decodeHTMLEntities.
export function decodeFlightCityNames(fdata) {
  if (!fdata || typeof fdata !== 'object') return fdata
  const dec = (v) => (typeof v === 'string' ? decodeHTMLEntities(v) : v)
  if (fdata.cities && typeof fdata.cities === 'object') {
    Object.keys(fdata.cities).forEach((code) => {
      const city = fdata.cities[code]
      if (city && typeof city === 'object') {
        if (city.city_name != null) city.city_name = dec(city.city_name)
        if (city.airport != null) city.airport = dec(city.airport)
      }
    })
  }
  if (Array.isArray(fdata.list)) {
    fdata.list.forEach((entry) => {
      if (entry && Array.isArray(entry.itineraries)) {
        entry.itineraries.forEach((it) => {
          if (it && it.depart_city != null) it.depart_city = dec(it.depart_city)
          if (it && it.arrival_city != null) it.arrival_city = dec(it.arrival_city)
        })
      }
    })
  }
  return fdata
}
export function pagePriceFormat(payment,displayPayment={},delimiter = "\n(",type=1)
{
  let res = '';
  if(notEmpty(payment)){
    let displayRes = '';
    let pRes = '';
    if(Math.abs(payment['amount']) > 0.01)
    {
       
       pRes=priceFormat(payment['currency']??'USD',payment['amount'],",", 2, 1);
    }

    if(notEmpty(displayPayment) && Math.abs(displayPayment['amount']) > 0.01)
    {
      displayRes = priceFormat(displayPayment['currency']??'USD',displayPayment['amount'],",", 2, 1);
    }
    let clsdelimiter = delimiter.includes('(')?')':'';
    if(1==type){
      res = pRes
      if(res != '' && displayRes!=''){
        res += delimiter + displayRes + clsdelimiter
      }
      else{
         res += displayRes
      }
    }
    else{
      if(pRes!='' && displayRes!=''){
        res = displayRes + delimiter + pRes + clsdelimiter
      }
      else{
        res = displayRes+pRes
      }      
    }
  }
  return res;
}